import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { Tooltip } from "@blueprintjs/core";
import { PopoverPosition } from "@blueprintjs/core";
import TextTruncate from "components/TextTruncate";
import ThreeDotsMenu from "components/ThreeDotsMenu";
import IconButton from "components/IconButton";
import DefaultThreeDotsMenuItem from "components/ThreeDotsMenu/Components/DefaultThreeDotsMenuItem";
import { ColorMapType, getPointColor, getValueWithinRange, } from "rs-components/Spaceviewer/utils";
import ResultInfoMessage, { ResultInfoMessageType } from "components/ResultInfoMessage";
import CreateEntityFromResultElementDialog from "pages/RS-Workspace/components/Results/components/CreateEntityFromResultElementDialog";
import CreateVariantFromResultElementDialog from "pages/RS-Workspace/components/Results/components/CreateVariantFromResultElementDialog";
import { getTextContentFromHtml, numberToFixedPointNotation, renderNumberValue } from "utils/string";
import { SpaceviewerAxisType } from "shared/types";
import { isNotEmpty } from "utils/validate";
import { N_A } from "shared/rsDictionary";
import { prepareSpaceviewerSource } from "store/rs-workspace/utils/results.utils";
import { copyToClipboard } from "utils/data";
import useMessageTimer from "hooks/useMessageTimer";
import { EntitiesCreatableFromResultElement, ResultElementParameterType, ResultElementSourceType, ResultSetElementType, SimulationRunState, } from "store/rs-workspace/rs-workspace.types";
import { setCreateEntityFromResultElementDialogData } from "store/rs-workspace/actions/results.actions";
import { createEntityFromResultElementDialogDataSelector } from "store/rs-workspace/selectors/results.selectors";
import { parameterTypesSelector, processVariablesOptionsSelector, processVariablesSelector, } from "store/global/global.selectors";
import { persistedInsightsDataSelector, workspaceInsightsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { NewEntityCreationOption } from "shared/labels";
import ErrorIcon from "icons/info_triangle.svg";
import PlusIcon from "icons/plus.svg";
import DuplicateIcon from "icons/icn-duplicate.svg";
import * as styles from "../legend-styles.module.scss";
const ResultElementLegend = ({ legendData, selectedPoint, parameters, slidersValues, range, source, elementId, colorMapType, totalRunsCount, sourceResultState, }) => {
    const { data: { response: { value } = {}, values } = {} } = selectedPoint ?? {};
    const dispatch = useDispatch();
    const [newEntityType, setNewEntityType] = useState(null);
    const [threeDotsMenuOpen, setThreeDotsMenuOpen] = useState(false);
    const [showCopiedToClipboardMessage, setShowCopiedToClipboardMessage] = useMessageTimer();
    const pageInEditMode = useSelector(isEditableSelector);
    const createEntityDialogData = useSelector(createEntityFromResultElementDialogDataSelector);
    const parameterTypes = useSelector(parameterTypesSelector);
    const variableOptionsList = useSelector(processVariablesOptionsSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const simulations = useSelector(workspaceSimulationsSelector);
    const insights = useSelector(workspaceInsightsSelector);
    const persistedInsights = useSelector(persistedInsightsDataSelector);
    const processVariables = useSelector(processVariablesSelector);
    const rangedValue = useMemo(() => value === undefined
        ? undefined
        : getValueWithinRange(value === null ? N_A : numberToFixedPointNotation(value, 6), range), [range, value]);
    const hasFailedRuns = useMemo(() => !!selectedPoint?.data?.errorCount && selectedPoint?.data?.errorCount > 0, [selectedPoint]);
    const invertColor = useMemo(() => {
        if (!isNotEmpty(rangedValue)) {
            return;
        }
        const rangedValueNumber = rangedValue;
        switch (colorMapType) {
            case ColorMapType.Low:
                return rangedValueNumber > 60;
            case ColorMapType.Middle:
                return rangedValueNumber < 30 || rangedValueNumber > 70;
            case ColorMapType.High:
                return rangedValueNumber < 60;
        }
    }, [colorMapType, rangedValue]);
    const canCreateEntityFromSource = useMemo(() => sourceResultState === SimulationRunState.FINISHED &&
        source?.source?.type === ResultElementSourceType.Simulation &&
        isNotEmpty(value), [source?.source?.type, sourceResultState, value]);
    const menuItems = useMemo(() => [
        {
            text: NewEntityCreationOption.Variant,
            icon: React.createElement(PlusIcon, { width: 15, height: 15 }),
            onClick: () => {
                setNewEntityType(EntitiesCreatableFromResultElement.Variant);
                dispatch(setCreateEntityFromResultElementDialogData({
                    elementId: elementId ?? 0,
                    sourceId: source?.id ?? 0,
                }));
            },
        },
        {
            text: NewEntityCreationOption.Recipe,
            icon: React.createElement(PlusIcon, { width: 15, height: 15 }),
            onClick: () => {
                setNewEntityType(EntitiesCreatableFromResultElement.Recipe);
                dispatch(setCreateEntityFromResultElementDialogData({
                    elementId: elementId ?? 0,
                    sourceId: source?.id ?? 0,
                }));
            },
        },
        {
            text: NewEntityCreationOption.CellModel,
            icon: React.createElement(PlusIcon, { width: 15, height: 15 }),
            onClick: () => {
                setNewEntityType(EntitiesCreatableFromResultElement.CellModel);
                dispatch(setCreateEntityFromResultElementDialogData({
                    elementId: elementId ?? 0,
                    sourceId: source?.id ?? 0,
                }));
            },
        },
    ], [dispatch, elementId, source?.id]);
    const createDialogParameterValues = useMemo(() => parameters.map((parameter, index) => {
        const rawValue = values === undefined ? slidersValues?.[index] : values?.[index];
        const { steps } = parameter;
        const regularParameter = {
            name: parameter.originalName,
            type: parameter.sourceType,
            value: rawValue,
        };
        const alternativeFlowParameter = {
            name: parameter.originalName,
            type: ResultElementParameterType.ALTERNATIVE_FLOW,
            flowIndex: steps?.find(({ value }) => value === rawValue)?.value ?? 0,
        };
        return parameter.type === SpaceviewerAxisType.FLOAT ? regularParameter : alternativeFlowParameter;
    }), [parameters, slidersValues, values]);
    const getValue = useCallback((parameter, index) => {
        const rawValue = values === undefined ? slidersValues?.[index] : values?.[index];
        if (parameter.type === SpaceviewerAxisType.FLOAT) {
            return renderNumberValue(rawValue);
        }
        const { steps } = parameter;
        return steps?.find(({ value }) => value === rawValue)?.label;
    }, [slidersValues, values]);
    const onCopy = useCallback(() => {
        const sourceVariable = source?.source?.type === ResultElementSourceType.Insight ? "Insight" : legendData.sourceVariable;
        const sourceValue = isNotEmpty(value) ? renderNumberValue(value) : N_A;
        if (!source) {
            return;
        }
        const preparedSource = prepareSpaceviewerSource(source, variableOptionsList, parameterTypes, simulations, insights, persistedInsights, processVariables, recipes);
        const sourceUnit = preparedSource?.units
            ? ` ${getTextContentFromHtml(preparedSource?.units?.label)}`
            : "";
        const header = `${sourceVariable}: ${sourceValue}${sourceUnit}\r\n`;
        const parametersText = parameters?.reduce((acc, item, itemIndex) => {
            const parameterUnit = item.unit ? ` ${getTextContentFromHtml(item.unit)}` : "";
            const text = `${item.name} - ${getValue(item, itemIndex)}${parameterUnit}`;
            return `${acc}\r\n${text}`;
        }, "");
        copyToClipboard(`${header}${parametersText}`);
        setShowCopiedToClipboardMessage(true);
    }, [
        getValue,
        insights,
        legendData.sourceVariable,
        parameterTypes,
        parameters,
        persistedInsights,
        processVariables,
        recipes,
        setShowCopiedToClipboardMessage,
        simulations,
        source,
        value,
        variableOptionsList,
    ]);
    const renderBiasedInfoTooltip = useCallback(() => {
        return (React.createElement("div", { className: styles.biasedInfo },
            React.createElement(Tooltip, { minimal: true, content: React.createElement(React.Fragment, null,
                    React.createElement("p", { className: styles["tooltip-title"] }, "Data is biased"),
                    React.createElement("p", null,
                        selectedPoint?.data?.errorCount,
                        " out of ",
                        totalRunsCount,
                        " runs failed"),
                    React.createElement("p", null, selectedPoint?.data?.errors[0])), position: PopoverPosition.BOTTOM, portalClassName: styles.tooltipPortal, popoverClassName: styles.tooltip, boundary: "clippingParents", openOnTargetFocus: false },
                React.createElement(ErrorIcon, { className: styles["error-icon"] }))));
    }, [selectedPoint?.data, totalRunsCount]);
    const renderCreateEntityDialogs = useMemo(() => {
        if (createEntityDialogData.elementId !== elementId || createEntityDialogData.sourceId !== source?.id) {
            return null;
        }
        return newEntityType === EntitiesCreatableFromResultElement.Variant ? (React.createElement(CreateVariantFromResultElementDialog, { simulationId: source.source.id ?? 0, adjustmentId: source.source.adjustmentId ?? 0, sourceId: source.id ?? 0, parameterValues: createDialogParameterValues, resultElementType: ResultSetElementType.SpaceViewer, elementId: elementId })) : (React.createElement(CreateEntityFromResultElementDialog, { isOpen: true, sourceId: source?.id, parameterValues: createDialogParameterValues, entityType: newEntityType ?? EntitiesCreatableFromResultElement.Recipe, resultElementType: ResultSetElementType.SpaceViewer, elementId: elementId, entityId: source.source.id ?? 0, onClose: () => dispatch(setCreateEntityFromResultElementDialogData({ elementId: null, sourceId: null })) }));
    }, [
        createDialogParameterValues,
        createEntityDialogData.elementId,
        createEntityDialogData.sourceId,
        dispatch,
        elementId,
        newEntityType,
        source?.id,
        source?.source,
    ]);
    return (React.createElement("div", { className: styles["legend-table"] },
        React.createElement("table", null,
            React.createElement("colgroup", null,
                React.createElement("col", { className: styles["table-parameter"] })),
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", null, "Parameter"),
                    React.createElement("th", null,
                        "Value",
                        React.createElement("span", { className: styles.controls },
                            !!selectedPoint && (React.createElement(IconButton, { title: "Copy", icon: React.createElement(DuplicateIcon, null), classes: { root: styles.copy }, onClick: onCopy })),
                            pageInEditMode && canCreateEntityFromSource && (React.createElement(ThreeDotsMenu, { name: "Actions", classes: {
                                    className: classNames(styles.threeDotsMenu, {
                                        [styles["threeDotsMenu-opened"]]: threeDotsMenuOpen,
                                    }),
                                }, items: menuItems.map((item, index) => (React.createElement(DefaultThreeDotsMenuItem, { key: index, closeMenuOnClick: () => setThreeDotsMenuOpen(false), ...item }))), isOpen: threeDotsMenuOpen, setIsOpen: setThreeDotsMenuOpen })))))),
            React.createElement("tbody", null,
                React.createElement("tr", null,
                    React.createElement("td", null,
                        React.createElement(TextTruncate, { className: styles["table-parameter-name"], text: legendData.sourceVariable })),
                    React.createElement("td", { className: classNames(styles["insight-value"], {
                            [styles["insight-value-inverted"]]: invertColor,
                        }), style: rangedValue === undefined || rangedValue === N_A
                            ? {}
                            : { backgroundColor: getPointColor(rangedValue, colorMapType) } },
                        isNotEmpty(value) ? renderNumberValue(value) : N_A,
                        hasFailedRuns && renderBiasedInfoTooltip())),
                (parameters || []).map((parameter, index) => (React.createElement("tr", { key: `${parameter.id}-${parameter.name}-${index}` },
                    React.createElement("td", null,
                        React.createElement(TextTruncate, { className: styles["table-parameter-name"], text: parameter.name })),
                    React.createElement("td", null, getValue(parameter, index))))))),
        React.createElement(ResultInfoMessage, { type: ResultInfoMessageType.COPIED_TO_CLIPBOARD, visible: showCopiedToClipboardMessage, onClose: () => setShowCopiedToClipboardMessage(false), className: styles["copied-to-clipboard-message"] }),
        renderCreateEntityDialogs));
};
export default ResultElementLegend;
